/** biome-ignore-all lint/a11y/useValidAnchor: custom link */
import cn from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { CookiesPolicyModal } from 'sonata-platform-components';

import './pageFooter.styles.scss';

type LinkConfig = {
  labelId: string;
  defaultLabel: string;
  href?: string;
  onClick?: () => void;
};

type Props = {
  variant?: 'dark' | 'light';
  /** Optional callback to open the FAQ section. When provided, the FAQs link becomes active. */
  onFaqOpen?: () => void;
};

export function PageFooter({ variant = 'dark', onFaqOpen }: Props) {
  const { formatMessage } = useIntl();
  const [showCookiesModal, setShowCookiesModal] = useState(false);
  const handleCookiesSettingsClick = () => {
    setShowCookiesModal(true);
  };

  const linksConfig = [
    {
      defaultLabel: 'FAQs',
      labelId: 'pageFooter.links.faqs',
      onClick: onFaqOpen
    },
    {
      defaultLabel: 'Legal Notice',
      href: 'https://cdn.prod.website-files.com/5fdc7d40015e9e207219e12f/643810aea2612c76e5df2dde_2023-04-13_Taptap_Legal_Notice.pdf',
      labelId: 'pageFooter.links.legalNotice'
    },
    {
      defaultLabel: 'Cookies Settings',
      labelId: 'pageFooter.links.cookiesSettings',
      onClick: handleCookiesSettingsClick
    },
    {
      defaultLabel: 'Terms & Conditions',
      href: 'https://cdn.prod.website-files.com/5fdc7d40015e9e207219e12f/67aa2bdf61f5a26d657e99b9_2025%2001%2001%20Taptap%20Platform.%20General%20T%26C_v25_01_ENG-ESP.pdf',
      labelId: 'pageFooter.links.termsAndConditions'
    },
    {
      defaultLabel: 'Sonata Privacy Policy',
      href: 'https://cdn.sonataplatform.com/legal/sonata_privacy_policy_september_2025.pdf',
      labelId: 'pageFooter.links.sonataPrivacyPolicy'
    },
    {
      defaultLabel: 'Taptap Privacy Policy',
      href: 'https://cdn.sonataplatform.com/legal/taptap_privacy_policy_september_2025.pdf',
      labelId: 'pageFooter.links.taptapPrivacyPolicy'
    },
    {
      defaultLabel: 'Corporate page',
      href: 'https://www.taptapdigital.com/',
      labelId: 'pageFooter.links.corporatePage'
    }
  ];

  const handleGenericLinkClick = (linkConfig: LinkConfig) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (linkConfig.onClick) {
      linkConfig.onClick();
    } else if (linkConfig.href) {
      window.open(linkConfig.href, '_blank');
    }
  };

  return (
    <footer className={cn('pageFooter', { 'pageFooter--light': variant === 'light' })}>
      <div className="pageFooter__logo">
        <img
          alt="taptapLogo"
          id="taptapLogo"
          src={`/assets/images/logos/${variant === 'light' ? 'logoTapTap_dark.png' : 'logoTapTap.png'}`}
        />
      </div>
      <div className="pageFooter__links">
        {linksConfig.map(linkConfig => (
          <a
            className="pageFooter__link"
            id={linkConfig.labelId}
            key={linkConfig.labelId}
            onClick={handleGenericLinkClick(linkConfig)}
          >
            {formatMessage({
              defaultMessage: linkConfig.defaultLabel,
              id: linkConfig.labelId
            })}
          </a>
        ))}
      </div>
      <div className="pageFooter__company">
        ©{new Date().getFullYear()}{' '}
        {formatMessage({
          defaultMessage: 'TAPTAP DIGITAL, S.L.',
          id: 'pageFooter.companyName'
        })}
      </div>
      <CookiesPolicyModal
        id="cookiesPolicyModal"
        isOpen={showCookiesModal}
        onClose={() => setShowCookiesModal(false)}
        policyUrl="https://cdn.sonataplatform.com/legal/cookies_policy_september_2025.pdf"
      />
    </footer>
  );
}
